import { datadogRum } from '@datadog/browser-rum';
import { useCallback } from 'react';

import { useMember } from '../../queries/member';

import { collectiveTracking } from './utils';

import useAuthSelectors from 'modules/Auth/AuthProvider/useAuthSelector';

function useEventTracking() {
    const { isAuthenticated, user, isEmployee, isImpersonating } = useAuthSelectors(
        'isAuthenticated',
        'user',
        'isEmployee',
        'isImpersonating'
    );
    const userId = user?.pk;
    const isStaff = isEmployee;
    const { data: member } = useMember({ enabled: isAuthenticated && !isEmployee });

    // Memoized because ~30 effects list it as a dependency. As a plain inner function it got a new
    // identity every render, so any of those effects without a ref guard re-fired its event on every
    // render of its host -- `Document Upload Section Started` averaged over 100 events per member.
    const sendTrackingEvent = useCallback(
        (eventName: string, props: Record<string, unknown> = {}) => {
            if (!isAuthenticated) {
                datadogRum.addAction('sendTrackingEvent called without authentication', {
                    eventName,
                });
            }

            collectiveTracking.track({
                name: eventName,
                properties: {
                    acting_user_id: userId,
                    is_staff: isStaff,
                    is_impersonating: isImpersonating,
                    ...(isStaff && { staff_id: userId }),
                    ...(!isStaff &&
                        member && {
                            member_id: member.id,
                            member_uuid: member.uuid,
                            member_usertype: member.usertype,
                            member_membership_tier: member.membership_tier,
                            member_stage: member.stage,
                            member_status: member.status,
                        }),
                    ...props,
                },
            });
        },
        [isAuthenticated, userId, isStaff, isImpersonating, member]
    );

    return { sendTrackingEvent };
}

export default useEventTracking;
